/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import DataTable from "examples/Tables/DataTable";

//React Hooks and Others
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import { setEditAdmin, setNoHereAdmin } from "slices";

import Swal from "sweetalert2";
import { ADQ_EDIT_URL, ADQ_LIST_URL, ADQ_NEW_URL } from "config/bases_url";
import PropTypes from "prop-types";
import {
  useDeleteAdqMutation,
  useGetAdqsQuery,
} from "services/catalogo/adq.service";
import { CircularProgress, Icon, Tooltip } from "@mui/material";
import ComponentsPrivatization, {
  removeColumns,
} from "config/component_privatization";
import { COORDINADOR_ALMACEN } from "roles";
import { style_icon } from "helpers/global_vars";
import { useRedirectForm } from "hooks/useRedirectForm";
import usePagination from "hooks/usePagination";

function DataTableAdqs({ tableHeader }) {
  const dispatch = useDispatch();

  const {
    offset,
    setOffset,
    limit,
    busqueda,
    setBusqueda,
    handleNext,
    handlePrevious,
  } = usePagination();

  const {
    data: adqs,
    isLoading: isLoadingG,
    isError: isErrorG,
    error: errorG,
  } = useGetAdqsQuery(
    {
      filter_params: {
        buscar: busqueda,
        offset: offset,
        limit: limit,
        query: "{id,nombre}",
      },
      shape: {
        id: "id",
        nombre: "nombre",
      },
    },
    {
      refetchOnReconnect: true,
    }
  );

  //constantes para manejar la paginacion
  const dataAdq = adqs?.results ? adqs.results : adqs;
  const countTotal = adqs?.count;
  const previous = adqs?.previous;
  const next = adqs?.next;

  const [
    deleteAdq,
    {
      isSuccess: isSuccessD,
      isLoading: isLoadingD,
      isError: isErrorD,
      error: errorD,
    },
  ] = useDeleteAdqMutation();

  const dataTable = {
    columns: [
      {
        Header: "Métodos de Adquisición",
        accessor: "nombre",
      },
      {
        Header: "Acciones",
        accessor: "actions",
        width: "10%",
        Cell: ({ row }) => {
          const { original } = row;

          return (
            <ComponentsPrivatization permitted={COORDINADOR_ALMACEN}>
              <MDBox display="flex">
                <MDBox px={0.2}>
                  <Tooltip placement="top" title="Editar">
                    <Icon
                      style={style_icon}
                      onClick={() => handleClickEdit(original)}
                      fontSize="small"
                    >
                      edit
                    </Icon>
                  </Tooltip>
                </MDBox>
                {original.borrable && (
                  <MDBox px={0.2}>
                    <Tooltip placement="top" title="Eliminar">
                      <Icon
                        style={style_icon}
                        onClick={(event) => handleClickErase(event, original)}
                        fontSize="small"
                      >
                        remove_circle
                      </Icon>
                    </Tooltip>
                  </MDBox>
                )}
              </MDBox>
            </ComponentsPrivatization>
          );
        },
      },
    ],
    rows: dataAdq ?? [],
  };

  useRedirectForm(
    isLoadingG,
    null,
    isErrorG,
    errorG,
    "Hubo problemas para cargar la información",
    ADQ_LIST_URL
  );

  useRedirectForm(
    isLoadingD,
    isSuccessD,
    isErrorD,
    errorD,
    "Método de Adquisición eliminado",
    ADQ_LIST_URL
  );

  const handleClickErase = (event, adq) => {
    event.stopPropagation();

    Swal.fire({
      title: "¿Estás seguro?",
      text: `¡Si eliminas el método de adquisicion: "${adq.nombre}", esta acción no se podrá revertir!`,
      icon: "warning",
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Eliminar",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteAdq(adq.id);
      }
    });
  };

  const navigate = useNavigate();

  const handleClickEdit = (adq) => {
    dispatch(setNoHereAdmin(false));
    navigate(ADQ_EDIT_URL(adq.id));
    dispatch(setEditAdmin(adq));
  };

  const handleClickCreate = () => {
    dispatch(setEditAdmin(null));
    dispatch(setNoHereAdmin(false));
  };

  return (
    <MDBox pt={6} pb={3}>
      <Card>
        <MDBox
          display="flex"
          justifyContent={"space-between"}
          alignItems={"center"}
          m={2}
          p={3}
        >
          <MDTypography variant="h5" fontWeight="medium">
            {tableHeader}
          </MDTypography>
          <ComponentsPrivatization permitted={COORDINADOR_ALMACEN}>
            <Link to={ADQ_NEW_URL}>
              <MDButton
                variant="gradient"
                color="info"
                size="medium"
                onClick={handleClickCreate}
              >
                nuevo
              </MDButton>
            </Link>
          </ComponentsPrivatization>
        </MDBox>
        <MDBox display="flex" justifyContent={"center"} alignItems={"center"}>
          {isLoadingG ? (
            <CircularProgress sx={{ marginBottom: "3rem" }} color="inherit" />
          ) : (
            <DataTable
              table={removeColumns(
                dataTable,
                () =>
                  !JSON.parse(sessionStorage.getItem("user")).groups.some(
                    (role) => role.name === COORDINADOR_ALMACEN
                  ),
                ["actions"]
              )}
              canSearch
              paginationApi
              countTotal={countTotal}
              next={next}
              previous={previous}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
              offset={offset}
              setOffset={setOffset}
              setBusqueda={setBusqueda}
            />
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}

DataTableAdqs.propTypes = {
  tableHeader: PropTypes.string.isRequired,
};

export default DataTableAdqs;
